import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { Share2, Check, Copy, Coffee } from "lucide-react";
import posthog from "posthog-js";
import { Mascot } from "@/components/Mascot";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateCafeData, invalidateUserStats } from "@/lib/queryKeys";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useStreak } from "@/hooks/useStreak";
import { useContributorTier, getNextTierProgress } from "@/hooks/useContributorTier";
import { useBadgeProgress } from "@/hooks/useBadgeProgress";
import { useAllBadges } from "@/hooks/useBadges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  DRINK_OPTIONS,
  type DrinkOption,
  bucketPrice,
  resolveDrinkOrder,
  validateDrinkPrice,
  buildDrinkPatch,
  orderCategory,
} from "@/lib/drinkReport";

interface CheckInCelebrationProps {
  cafeId: string;
  cafeName: string;
  open: boolean;
  onDone: () => void;
  vibeSubmitted?: boolean;
  checkInId?: string | null;
}

const QUICK_VIBES = [
  { tag: "Focused", emoji: "🎯" },
  { tag: "Chill", emoji: "😌" },
  { tag: "Social", emoji: "🤝" },
  { tag: "Chaotic", emoji: "🎉" },
];

/**
 * Compact progress pill used in the check-in celebration for both the
 * "next badge" teaser and the contributor-tier fallback. Presentational only.
 */
export function ProgressTeaser({
  label,
  trailing,
  pct,
}: {
  label: React.ReactNode;
  trailing: string;
  pct: number;
}) {
  return (
    <div className="w-full rounded-xl px-3 py-2.5" style={{ backgroundColor: "hsl(25 25% 91%)" }}>
      <div className="flex items-center justify-between mb-1.5">
        <p
          className="text-[10px] font-semibold uppercase tracking-wide flex items-center gap-1"
          style={{ color: "hsl(25 35% 45%)" }}
        >
          {label}
        </p>
        <p className="text-[10px]" style={{ color: "hsl(25 25% 55%)" }}>
          {trailing}
        </p>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "hsl(25 20% 82%)" }}>
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: "hsl(25 55% 48%)" }}
        />
      </div>
    </div>
  );
}

export function CheckInCelebration({
  cafeId,
  cafeName,
  open,
  onDone,
  vibeSubmitted = false,
  checkInId,
}: CheckInCelebrationProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [visible, setVisible] = useState(false);
  const [quickVibeSaved, setQuickVibeSaved] = useState(false);
  const [savingVibe, setSavingVibe] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  // Optional drink/price reporter — collapsed by default; never blocks Done.
  const [drinkOpen, setDrinkOpen] = useState(false);
  const [selectedDrink, setSelectedDrink] = useState<DrinkOption | null>(null);
  const [otherDrinkText, setOtherDrinkText] = useState("");
  const [priceText, setPriceText] = useState("");
  const [savingDrink, setSavingDrink] = useState(false);
  const [drinkSaved, setDrinkSaved] = useState(false);
  const { data: streakData } = useStreak();
  const { data: contributorStats } = useContributorTier(user?.id ?? null);
  const tierProgress = getNextTierProgress(contributorStats ?? null);
  const { data: badgeProgressMap } = useBadgeProgress(user?.id ?? null);
  const { data: allBadges } = useAllBadges();

  const { data: cafeSlug } = useQuery({
    queryKey: ["cafe-slug", cafeId],
    queryFn: async () => {
      const { data } = await supabase
        .from("cafes")
        .select("slug")
        .eq("id", cafeId)
        .maybeSingle();
      return data?.slug ?? null;
    },
    enabled: !!cafeId && open,
    staleTime: Infinity,
  });

  const { data: visitCount } = useQuery({
    queryKey: ["user-cafe-visits", cafeId, user?.id],
    queryFn: async () => {
      const { count } = await supabase
        .from("check_ins")
        .select("id", { count: "exact", head: true })
        .eq("cafe_id", cafeId)
        .eq("user_id", user!.id);
      return count;
    },
    enabled: !!user && open,
    staleTime: 30_000,
  });

  const { data: monthCount } = useQuery({
    queryKey: ["user-month-checkins", user?.id],
    queryFn: async () => {
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      const { count } = await supabase
        .from("check_ins")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user!.id)
        .gte("checked_in_at", startOfMonth.toISOString());
      return count;
    },
    enabled: !!user && open,
    staleTime: 30_000,
  });

  const handleShare = async () => {
    const surface = "checkin_celebration";
    const url = cafeSlug
      ? `https://groundedtheapp.com/cafe/${cafeSlug}`
      : "https://groundedtheapp.com";
    const shareText = `Working from ${cafeName} today ☕ Found it on Grounded.`;
    const canNative = !!navigator.share;
    try {
      posthog.capture("share_initiated", {
        surface,
        cafe_id: cafeId,
        cafe_name: cafeName,
        method_attempted: canNative ? "native" : "clipboard",
      });
    } catch { /* ignore */ }

    if (canNative) {
      try {
        await navigator.share({ title: cafeName, text: shareText, url });
        try {
          posthog.capture("share_completed", {
            surface,
            cafe_id: cafeId,
            cafe_name: cafeName,
            method: "native_url",
          });
        } catch { /* ignore */ }
      } catch (err) {
        const name = (err as DOMException)?.name;
        if (name === "AbortError") {
          try {
            posthog.capture("share_cancelled", { surface, cafe_id: cafeId, cafe_name: cafeName });
          } catch { /* ignore */ }
        } else {
          try {
            posthog.capture("share_failed", {
              surface,
              cafe_id: cafeId,
              cafe_name: cafeName,
              error_name: name ?? "unknown",
            });
          } catch { /* ignore */ }
        }
        // user cancelled or browser rejected — no-op (preserve existing behavior)
      }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareText} ${url}`);
        try {
          posthog.capture("share_completed", {
            surface,
            cafe_id: cafeId,
            cafe_name: cafeName,
            method: "clipboard",
          });
        } catch { /* ignore */ }
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2500);
      } catch (err) {
        try {
          posthog.capture("share_failed", {
            surface,
            cafe_id: cafeId,
            cafe_name: cafeName,
            error_name: (err as Error)?.name ?? "clipboard_error",
          });
        } catch { /* ignore */ }
        // clipboard denied — no-op
      }
    }
  };

  useEffect(() => {
    if (!open) {
      setVisible(false);
      setQuickVibeSaved(false);
      setShareCopied(false);
      setDrinkOpen(false);
      setSelectedDrink(null);
      setOtherDrinkText("");
      setPriceText("");
      setDrinkSaved(false);
      return;
    }
    setVisible(true);

    // canvas-confetti touches <canvas>/requestAnimationFrame, which can throw
    // inside some WebViews (notably iOS WKWebView). A synchronous throw here
    // would propagate to the app-level ErrorBoundary and crash the whole flow
    // to the "Oops" screen — so we isolate it. Confetti is purely decorative.
    try {
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#8FAE80', '#D4A574', '#C4956A', '#A4C295'],
        zIndex: 9999,
      });
    } catch (e) {
      console.warn("[CheckInCelebration] confetti failed (non-fatal):", e);
    }
  }, [open]);

  const handleQuickVibe = async (tag: string) => {
    if (!checkInId || savingVibe) return;
    setSavingVibe(true);
    try {
      await supabase
        .from("check_ins")
        .update({ vibe_tag: tag })
        .eq("id", checkInId);
      setQuickVibeSaved(true);
      // Vibe write can flip a skipped row into a counting visit.
      invalidateCafeData(queryClient, cafeId);
      if (user?.id) invalidateUserStats(queryClient, user.id);
    } catch {
      // fail silently — vibe is optional
    } finally {
      setSavingVibe(false);
    }
  };

  // Pure resolution / validation lives in src/lib/drinkReport.ts so it can be
  // unit-tested without React. Retain the local var names used by the JSX
  // below (priceProvided / priceValid / validPrice) so this stays a no-op edit.
  const resolvedDrink = resolveDrinkOrder(selectedDrink, otherDrinkText);
  const price = validateDrinkPrice(priceText);
  const priceProvided = price.provided;
  const priceValid = price.valid;
  const validPrice = price.value;
  const canSaveDrink = !savingDrink && priceValid && (resolvedDrink !== null || validPrice !== null);

  const handleSaveDrink = async () => {
    if (!checkInId || !canSaveDrink) return;
    setSavingDrink(true);
    const patch = buildDrinkPatch(resolvedDrink, validPrice);
    if (!patch) {
      setSavingDrink(false);
      return;
    }

    try {
      const { error } = await supabase
        .from("check_ins")
        .update(patch)
        .eq("id", checkInId);
      if (error) throw error;
      setDrinkSaved(true);
      // Drink write can flip a skipped row into a counting visit, and a DB
      // trigger recomputes the cafe's price level from these fields.
      invalidateCafeData(queryClient, cafeId);
      if (user?.id) invalidateUserStats(queryClient, user.id);
      try {
        posthog.capture("drink_price_reported", {
          cafe_id: cafeId,
          // canonical chip label, or "other" for free-text (never the raw text)
          order_category: orderCategory(selectedDrink),
          // bucketed only — never raw price
          price_bucket: validPrice !== null ? bucketPrice(validPrice) : null,
        });
      } catch {
        // analytics is best-effort
      }
    } catch {
      // fail silently — this is optional and must not break the celebration
    } finally {
      setSavingDrink(false);
    }
  };

  const handleDone = () => {
    setVisible(false);
    onDone();
  };

  const visitLabel = visitCount == null ? null : visitCount === 1 ? "First visit!" : `Visit #${visitCount}`;

  const getTimeMessage = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 11) return "You're in ✓ — now go order something that makes the morning worth it.";
    if (hour >= 11 && hour < 14) return "You're in ✓ — now go order something that makes the lunch hour worth it.";
    if (hour >= 14 && hour < 17) return "You're in ✓ — now go order something that makes the afternoon worth it.";
    return "You're in ✓ — now go order something that makes the night worth it.";
  };

  const showQuickVibe = !vibeSubmitted && !!checkInId && !quickVibeSaved;

  // Pick the actionable metric closest to completion for the progress teaser
  const tierTeaser = (() => {
    if (!tierProgress) return null;
    const { tier, progress } = tierProgress;
    const candidates = [
      { label: "spots added", ...progress.spots },
      { label: "reviews written", ...progress.reviews },
    ];
    // highest completion ratio wins — most motivating to surface
    // also require at least some progress so we don't show a 0% empty bar
    const best = candidates
      .filter((c) => c.current < c.target && c.current > 0)
      .sort((a, b) => b.current / b.target - a.current / a.target)[0];
    if (!best) return null;
    const pct = Math.min(100, Math.round((best.current / best.target) * 100));
    const remaining = best.target - best.current;
    return { tierName: tier.name, label: best.label, pct, remaining };
  })();

  // Pick the badge closest to completion for a concrete "next badge" teaser.
  // Only surface badges that are actually WITHIN REACH (a few steps away) —
  // a far-off goal like "586 focus min to go" is demotivating and usually
  // contextually irrelevant right after a check-in. When nothing's close, we
  // fall back to the tier teaser instead.
  const badgeTeaser = useMemo(() => {
    if (!badgeProgressMap || !allBadges) return null;
    const MAX_REMAINING = 5;
    const nameBySlug = new Map(allBadges.map((b) => [b.slug, b.name]));
    const best = Object.values(badgeProgressMap)
      .filter(
        (p) =>
          p.current > 0 &&
          p.current < p.threshold &&
          p.threshold - p.current <= MAX_REMAINING &&
          nameBySlug.has(p.slug)
      )
      .sort((a, b) => b.current / b.threshold - a.current / a.threshold)[0];
    if (!best) return null;
    return {
      name: nameBySlug.get(best.slug)!,
      pct: Math.min(100, Math.round(best.percentage)),
      remaining: best.threshold - best.current,
    };
  }, [badgeProgressMap, allBadges]);

  return (
    <Dialog open={visible} onOpenChange={(o) => { if (!o) handleDone(); }}>
      <DialogContent
        className="max-w-[320px] p-0 gap-0 max-h-[85dvh] overflow-y-auto border-0 shadow-2xl rounded-2xl [&>button]:hidden"
        style={{
          background: "linear-gradient(135deg, hsl(30 40% 97%), hsl(25 35% 93%))",
          border: "1px solid hsl(30 30% 88%)",
        }}
      >
        <VisuallyHidden>
          <DialogTitle>Check-in confirmed</DialogTitle>
        </VisuallyHidden>

        <div className="flex flex-col items-center text-center py-6 px-6 animate-fade-in gap-3">
          {/* Mascot */}
          <div
            className="h-16 w-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "hsl(25 40% 90%)" }}
          >
            <Mascot mood="happy" size="md" className="!h-12 !w-12" />
          </div>

          <div
            className="text-2xl animate-bounce"
            style={{ animationIterationCount: 2, animationDuration: "0.6s" }}
          >
            🎉
          </div>

          <div>
            <h3
              className="font-display text-lg font-bold mb-0.5"
              style={{ color: "hsl(25 50% 22%)" }}
            >
              You're checked in!
            </h3>
            <p
              className="text-sm font-medium"
              style={{ color: "hsl(25 40% 35%)" }}
            >
              {cafeName}
            </p>
            <p className="text-xs mt-1" style={{ color: "hsl(25 30% 55%)" }}>
              {getTimeMessage()}
            </p>
          </div>

          {/* Stat badges */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {visitLabel && (
              <span
                className="text-xs font-semibold px-2.5 py-1 rounded-full"
                style={{ backgroundColor: "hsl(25 45% 88%)", color: "hsl(25 50% 30%)" }}
              >
                {visitLabel}
              </span>
            )}
            {streakData && streakData.currentStreak > 0 && (
              <span
                className="text-xs font-semibold px-2.5 py-1 rounded-full"
                style={{ backgroundColor: "hsl(35 90% 90%)", color: "hsl(25 70% 30%)" }}
              >
                🔥 {streakData.currentStreak}-week streak!
              </span>
            )}
          </div>

          {streakData && streakData.currentStreak === 0 && visitCount === 1 && (
            <p className="text-xs font-medium" style={{ color: "hsl(25 50% 40%)" }}>
              Streak started! Come back next week to keep it going.
            </p>
          )}

          {/* Quick vibe picker — only when they skipped the full dialog */}
          {showQuickVibe && (
            <div className="w-full">
              <p className="text-xs font-semibold mb-2" style={{ color: "hsl(25 40% 40%)" }}>
                How's the vibe right now?
              </p>
              <div className="grid grid-cols-4 gap-1.5">
                {QUICK_VIBES.map(({ tag, emoji }) => (
                  <button
                    key={tag}
                    onClick={() => handleQuickVibe(tag)}
                    disabled={savingVibe}
                    className="flex flex-col items-center gap-1 py-2 px-1 rounded-xl text-[10px] font-medium transition-all hover:scale-105 active:scale-95"
                    style={{
                      backgroundColor: "hsl(25 30% 92%)",
                      color: "hsl(25 40% 35%)",
                    }}
                  >
                    <span className="text-lg">{emoji}</span>
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {quickVibeSaved && (
            <p className="text-xs font-medium" style={{ color: "hsl(145 40% 35%)" }}>
              Vibe saved! 🙌
            </p>
          )}

          {vibeSubmitted && (
            <p className="text-xs" style={{ color: "hsl(145 35% 40%)" }}>
              Vibe report submitted ✓
            </p>
          )}

          {/* Optional drink/price reporter — collapsed by default. Skippable;
              never blocks Done. Only renders when we have a check-in to attach
              to and the user hasn't already saved one this celebration. */}
          {!!checkInId && !drinkSaved && (
            <div className="w-full">
              {!drinkOpen ? (
                <button
                  onClick={() => setDrinkOpen(true)}
                  className="w-full flex items-center gap-3 rounded-xl border border-espresso/15 bg-gradient-to-br from-cream/80 to-background p-3 shadow-sm transition-all hover:opacity-90 active:scale-95 text-left"
                >
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Coffee className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold text-foreground">
                      What'd you order?
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      Help others know the price. Tap to share.
                    </span>
                  </div>
                </button>
              ) : (
                <div className="w-full rounded-xl p-3 text-left space-y-2.5" style={{ backgroundColor: "hsl(25 25% 91%)" }}>
                  <p className="text-xs font-semibold" style={{ color: "hsl(25 40% 40%)" }}>
                    What'd you order?
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {DRINK_OPTIONS.map((opt) => {
                      const active = selectedDrink === opt;
                      return (
                        <button
                          key={opt}
                          onClick={() => setSelectedDrink(opt)}
                          className="text-[11px] font-medium px-2.5 py-1 rounded-full transition-all active:scale-95"
                          style={{
                            backgroundColor: active ? "hsl(25 60% 45%)" : "hsl(25 30% 96%)",
                            color: active ? "white" : "hsl(25 40% 35%)",
                            border: "1px solid hsl(25 20% 82%)",
                          }}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {selectedDrink === "Other" && (
                    <Input
                      value={otherDrinkText}
                      onChange={(e) => setOtherDrinkText(e.target.value)}
                      placeholder="e.g. Cortado"
                      maxLength={60}
                      className="h-8 text-xs"
                    />
                  )}
                  <p className="text-xs font-semibold pt-1" style={{ color: "hsl(25 40% 40%)" }}>
                    How much? (optional)
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm" style={{ color: "hsl(25 40% 40%)" }}>$</span>
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.25"
                      min={1}
                      max={50}
                      value={priceText}
                      onChange={(e) => setPriceText(e.target.value)}
                      placeholder="5.50"
                      className="h-8 text-xs"
                    />
                  </div>
                  {priceProvided && !priceValid && (
                    <p className="text-[11px]" style={{ color: "hsl(0 60% 40%)" }}>
                      Enter a price between $1 and $50
                    </p>
                  )}
                  <div className="flex gap-2 pt-1">
                    <Button
                      onClick={() => setDrinkOpen(false)}
                      size="sm"
                      variant="ghost"
                      className="flex-1 h-8 text-xs"
                    >
                      Skip
                    </Button>
                    <Button
                      onClick={handleSaveDrink}
                      disabled={!canSaveDrink}
                      size="sm"
                      className="flex-1 h-8 text-xs"
                      style={{ backgroundColor: "hsl(25 60% 45%)", color: "white" }}
                    >
                      {savingDrink ? "Saving…" : "Save"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {drinkSaved && (
            <p className="text-xs font-medium" style={{ color: "hsl(145 40% 35%)" }}>
              Thanks — saved ☕
            </p>
          )}



          {/* Next-badge teaser — concrete, dopamine-driving goal. Falls back
              to the contributor-tier teaser when no badge is within reach. */}
          {badgeTeaser ? (
            <ProgressTeaser
              label={<>🏅 {badgeTeaser.name}</>}
              trailing={`${badgeTeaser.remaining} to go`}
              pct={badgeTeaser.pct}
            />
          ) : tierTeaser ? (
            <ProgressTeaser
              label={tierTeaser.tierName}
              trailing={`${tierTeaser.remaining} ${tierTeaser.label} to go`}
              pct={tierTeaser.pct}
            />
          ) : null}

          {/* Stats link — shows month total with nav to profile */}
          {monthCount != null && monthCount >= 1 && (
            <button
              onClick={() => { handleDone(); navigate("/profile"); }}
              className="text-xs underline-offset-2 hover:underline transition-colors"
              style={{ color: "hsl(25 40% 48%)" }}
            >
              {monthCount} check-in{monthCount !== 1 ? "s" : ""} this month · See your stats →
            </button>
          )}

          {/* Share this spot */}
          <button
            onClick={handleShare}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium transition-all hover:opacity-80 active:scale-95"
            style={{
              backgroundColor: "hsl(25 25% 91%)",
              color: "hsl(25 50% 35%)",
            }}
          >
            {shareCopied ? (
              <>
                <Check className="h-4 w-4" style={{ color: "hsl(145 40% 38%)" }} />
                <span style={{ color: "hsl(145 40% 38%)" }}>Link copied!</span>
              </>
            ) : (
              <>
                {navigator.share ? (
                  <Share2 className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                Share this spot
              </>
            )}
          </button>

          <Button
            onClick={handleDone}
            className="w-full mt-1"
            size="sm"
            style={{
              backgroundColor: "hsl(25 60% 45%)",
              color: "white",
            }}
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
